import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  History, RefreshCw, ChevronUp, ChevronDown, AlertCircle, FileText,
  Check, Trash2, MessageSquare, Search, X, ClipboardCheck, ClipboardList,
  ListTodo, Copy, Eye, Send, Link2, ExternalLink, Loader2, Info, CalendarRange, Calendar,
  Download, FileDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { StoplightPill, setEncounterStatus } from './EncounterStoplight';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';

import HistoryDedup from './HistoryDedup';
import { getHistoryDisplayTitle, getDocTypeLabel, formatHistoryTime, formatRelativeHistoryTime } from '../../hooks/historyDisplayUtils';
import { HistoryTodoList } from './HistoryTodoList';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * HistoryPanel - Displays generation history with filters and batch operations
 * 
 * Extracted from App.js to reduce monolithic component size.
 * Handles: History list, filtering, search, batch operations, draft management
 */
export const HistoryPanel = ({
  // History state
  history,
  filteredHistory,
  showHistory,
  setShowHistory,
  isLoadingHistory,
  loadHistory,
  
  // History item operations
  loadHistoryItem,
  deleteHistoryItem,
  updateHistoryItem,
  selectedHistoryItem,
  
  // Filtering
  historyFilter,
  setHistoryFilter,
  historySearchQuery,
  setHistorySearchQuery,
  // Date-jump + sort order (new in May 2026)
  dateJump,
  setDateJump,
  sortOrder,
  setSortOrder,
  
  // Version condensing
  condenseVersions,
  setCondenseVersions,
  expandedGroups,
  setExpandedGroups,
  
  // Batch mode
  isBatchMode,
  setIsBatchMode,
  batchSelectedItems,
  setBatchSelectedItems,
  toggleBatchSelect,
  selectAllHistory,
  exitBatchMode,
  batchCopyNotesAndICD10s,
  batchDeleteItems,
  batchExportItems,
  quickExportToday,
  quickExportDrafts,
  letterheadLine1,
  letterheadLine2,
  letterheadAddress,
  
  // Draft management
  getPendingDraftsCount,
  markDraftsAsCompleted,
  
  // Sharing
  shareLetter,
  copyToClipboard,
  
  // UI state setters
  setShowOrdersReview,
  setShowWeekendCatchup,
  
  // Role
  effectiveRole,
  
  // Utility
  formatDate,
  getLetterTypeLabel,
}) => {
  // Handler to mark proposal as completed
  const markProposalComplete = async (entryId, proposalType, itemIndex = null, currentCompleted = false) => {
    try {
      await axios.post(`${API}/history/mark-proposal-completed`, {
        entry_id: entryId,
        proposal_type: proposalType,
        item_index: itemIndex,
        completed: !currentCompleted
      }, { withCredentials: true });
      
      loadHistory();
      toast.success(!currentCompleted ? 'Marked as signed off!' : 'Unmarked');
    } catch (error) {
      toast.error('Failed to update proposal status');
    }
  };

  // Calculate pending counts
  const pendingCount = history.filter(h => 
    (h.proposed_medications?.some(m => !m.completed)) || 
    (h.proposed_profile && !h.proposed_profile.completed)
  ).length;

  const draftsCount = getPendingDraftsCount();

  /**
   * Marks an entry Submitted to Genesis. If it has sibling draft versions of the
   * same encounter (same age+gender+date, not submitted), offers to delete them.
   */
  const handleSubmittedToggle = async (item, checked) => {
    if (!updateHistoryItem) return;
    // silent: visual feedback is the green SUBMITTED badge — no toast spam needed
    await updateHistoryItem(item.id, { completed: !!checked }, { silent: true });
    // Sync the Encounter Stoplight model so the live stoplight + StoplightPills
    // across the app immediately reflect the new green/yellow state.
    try { setEncounterStatus(item.id, checked ? 'green' : (item.clinical_note ? 'amber' : 'yellow')); } catch { /* ignore */ }
    if (!checked) return;
    try { window.dispatchEvent(new CustomEvent('writeright:note-submitted')); } catch { /* ignore */ }
    // Honor "don't ask again" preference for sibling-cleanup prompt
    if (localStorage.getItem('writeright_skip_sibling_cleanup') === 'true') return;
    const ts = new Date(item.timestamp || item.created_at);
    const dateKey = ts.toDateString();
    const age = item.patient_age;
    const gender = (item.patient_gender || '').substring(0, 1).toLowerCase();
    if (!age) return;
    const siblings = (history || []).filter(h => {
      if (!h.id || h.id === item.id) return false;
      if (h.completed || h.completed_and_submitted) return false;
      if (!h.patient_age || h.patient_age !== age) return false;
      if ((h.patient_gender || '').substring(0, 1).toLowerCase() !== gender) return false;
      const hts = new Date(h.timestamp || h.created_at);
      return hts.toDateString() === dateKey;
    });
    if (siblings.length === 0) return;
    const siblingIds = siblings.map(s => s.id);
    toast('Keep only the Submitted version?', {
      description: `Delete ${siblings.length} older draft version${siblings.length > 1 ? 's' : ''} of this encounter. The Submitted copy stays.`,
      duration: 8000,
      action: {
        label: `Delete ${siblings.length}`,
        onClick: async () => {
          let deleted = 0;
          for (const sid of siblingIds) {
            try {
              await axios.delete(`${API}/history/${sid}`, { withCredentials: true });
              deleted++;
            } catch { /* ignore */ }
          }
          if (loadHistory) loadHistory();
          toast.success(`Cleaned up ${deleted} older version${deleted !== 1 ? 's' : ''}`);
        },
      },
      cancel: {
        label: "Don't ask again",
        onClick: () => {
          localStorage.setItem('writeright_skip_sibling_cleanup', 'true');
          toast.info('Sibling cleanup prompt disabled. Re-enable in Settings.', { duration: 2500 });
        },
      },
    });
  };

  // Date range for batch selection
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDedup, setShowDedup] = useState(false);
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  // Feb 27, 2026 — Tick every 30s so the relative-time "X ago" pill stays
  // current without coupling re-renders to keystrokes elsewhere in the app.
  // Cheap: one state-update per 30s only triggers a re-render of HistoryPanel.
  // eslint-disable-next-line no-unused-vars
  const [_relativeTick, setRelativeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRelativeTick((n) => (n + 1) % 1000000), 30000);
    return () => clearInterval(id);
  }, []);

  // Scroll-to-date when calendar input changes (new May 5, 2026 — replaces
  // the old filter-by-date behaviour that hid surrounding context). Maps the
  // YYYY-MM-DD value from <input type="date" /> to the dateKey format used
  // by the date-group headers (`new Date(...).toDateString()`) and scrolls
  // the matching header into view with a brief amber ring pulse.
  useEffect(() => {
    if (!dateJump || !showHistory) return;
    const target = new Date(dateJump + 'T00:00:00');
    if (Number.isNaN(target.getTime())) return;
    const dateKey = target.toDateString();
    const timeoutId = setTimeout(() => {
      // Make sure the target date isn't collapsed
      setCollapsedDates(prev => {
        if (!prev.has(dateKey)) return prev;
        const next = new Set(prev); next.delete(dateKey); return next;
      });
      const el = document.querySelector(
        `[data-testid="date-group-${CSS.escape(dateKey)}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-amber-400/70', 'rounded-md');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-amber-400/70', 'rounded-md');
        }, 1800);
      } else {
        toast.info(`No encounters on ${target.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, { duration: 2500 });
      }
    }, 80);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateJump, showHistory]);

  // Count today's encounters for the quick export button
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayCount = history.filter(h => new Date(h.timestamp) >= todayStart).length;

  const selectByDateRange = () => {
    if (!dateFrom && !dateTo) {
      toast.error('Set at least one date');
      return;
    }
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : new Date(0);
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : new Date();
    const matching = new Set();
    history.forEach(h => {
      const ts = new Date(h.timestamp);
      if (ts >= from && ts <= to) matching.add(h.id);
    });
    if (matching.size === 0) {
      toast.info('No entries in that date range');
      return;
    }
    setBatchSelectedItems(matching);
    toast.success(`Selected ${matching.size} entries`);
  };

  const entriesWithPendingMeds = history.filter(h => h.proposed_medications?.some(m => !m.completed));
  const entriesWithPendingProfiles = history.filter(h => h.proposed_profile && !h.proposed_profile.completed);
  const totalPending = entriesWithPendingMeds.length + entriesWithPendingProfiles.length;

  const pendingDrafts = history.filter(h => {
    if (h.is_draft === true) return true;
    const hasNotes = h.original_notes && h.original_notes.trim().length > 10;
    const hasGeneratedNote = h.clinical_note || h.result?.clinical_note || h.result?.soap_note;
    return hasNotes && !hasGeneratedNote;
  });

  return (
    <Collapsible open={showHistory} onOpenChange={setShowHistory}>
      <Card className="border-border bg-card" data-testid="history-card">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-secondary/50 transition-colors">
            <CardTitle className="flex items-center justify-between text-foreground font-bold text-base">
              <div className="flex items-center gap-2 flex-wrap">
                <History className="w-4 h-4 text-primary shrink-0" />
                <span className="hidden sm:inline">Generation History</span>
                <span className="sm:hidden">History</span>
                <Badge variant="outline" className={`text-xs bg-primary/20 text-foreground border-primary/30 ${showHistory ? 'inline-flex' : 'hidden sm:inline-flex'}`}>
                  {historyFilter !== 'all' ? `${filteredHistory.length}/${history.length}` : history.length}
                </Badge>
                {/* Pending Proposals Badge */}
                {pendingCount > 0 && (
                  <Badge variant="outline" className={`text-xs bg-amber-500/20 text-amber-500 border-amber-500/40 animate-pulse ${showHistory ? 'inline-flex' : 'hidden sm:inline-flex'}`}>
                    {pendingCount} Pending
                  </Badge>
                )}
                {/* Pending Drafts Badge */}
                {draftsCount > 0 && (
                  <Badge variant="outline" className={`text-xs bg-blue-500/20 text-blue-500 border-blue-500/40 ${showHistory ? 'inline-flex' : 'hidden sm:inline-flex'}`}>
                    {draftsCount} Draft{draftsCount > 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {showHistory && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); loadHistory(); }} 
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoadingHistory ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                {showHistory ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 max-h-[500px] overflow-y-auto custom-scrollbar">
            {/* Provider to-do scratchpad — free-text reminders for charting work
                that doesn't have a history row yet (meds to pull from yesterday,
                profile to write, follow-ups). Persists in localStorage. */}
            <div className="mb-3">
              <HistoryTodoList variant="inline" />
            </div>
            {/* Pending Proposals Alert */}
            {totalPending > 0 && (
              <Collapsible defaultOpen={false} className="mb-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg">
                    <span className="flex items-center gap-2 text-xs text-amber-500">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {totalPending} Pending Proposal{totalPending > 1 ? 's' : ''}
                    </span>
                    <ChevronDown className="w-3 h-3 text-amber-500" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 p-2 bg-amber-500/5 rounded-lg border border-amber-500/20">
                    {/* List of pending medications */}
                    {entriesWithPendingMeds.map(entry => (
                      <div key={entry.id} className="space-y-1 mb-2">
                        <p className="text-xs text-muted-foreground font-medium">
                          {getHistoryDisplayTitle(entry)}
                        </p>
                        {entry.proposed_medications?.filter(m => !m.completed).map((med, idx) => {
                          const originalIndex = entry.proposed_medications.findIndex(m => m.name === med.name);
                          return (
                            <label 
                              key={idx} 
                              className="flex items-center gap-2 p-1.5 rounded hover:bg-amber-500/20 cursor-pointer transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox 
                                checked={med.completed || false}
                                onCheckedChange={() => markProposalComplete(entry.id, 'medication', originalIndex, med.completed)}
                                className="border-amber-500 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                              />
                              <span className="text-xs text-foreground flex-1">
                                {med.name || med.details || 'Medication'} 
                              </span>
                              <Badge variant="outline" className="text-[10px] bg-amber-500/20 border-amber-500/30 text-amber-500">Med</Badge>
                            </label>
                          );
                        })}
                      </div>
                    ))}
                    
                    {/* List of pending profiles */}
                    {entriesWithPendingProfiles.map(entry => (
                      <div key={entry.id + '-profile'} className="mb-2">
                        <p className="text-xs text-muted-foreground font-medium mb-1">
                          {getHistoryDisplayTitle(entry)}
                        </p>
                        <label 
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-orange-500/20 cursor-pointer transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox 
                            checked={entry.proposed_profile.completed || false}
                            onCheckedChange={() => markProposalComplete(entry.id, 'profile', null, entry.proposed_profile.completed)}
                            className="border-orange-500 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                          />
                          <span className="text-xs text-foreground flex-1">
                            Profile: {entry.proposed_profile.duration || 'Temp Profile'}
                          </span>
                          <Badge variant="outline" className="text-[10px] bg-orange-500/20 border-orange-500/30 text-orange-500">Profile</Badge>
                        </label>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
            
            {/* Pending Drafts Section */}
            {pendingDrafts.length > 0 && (
              <Collapsible defaultOpen={false} className="mb-3">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg">
                    <span className="flex items-center gap-2 text-xs text-blue-500">
                      <FileText className="w-3.5 h-3.5" />
                      {pendingDrafts.length} Draft{pendingDrafts.length > 1 ? 's' : ''} Pending Generation
                    </span>
                    <ChevronDown className="w-3 h-3 text-blue-500" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 p-2 bg-blue-500/5 rounded-lg border border-blue-500/20 space-y-2">
                    {/* Bulk Actions for Drafts */}
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-blue-500/20">
                      <span className="text-[10px] text-muted-foreground">{pendingDrafts.length} drafts</span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-2 text-[10px] text-green-500 hover:bg-green-500/10"
                          onClick={() => {
                            const draftIds = pendingDrafts.map(d => d.id).filter(Boolean);
                            markDraftsAsCompleted(draftIds);
                          }}
                          data-testid="mark-all-completed-btn"
                        >
                          <Check className="w-2.5 h-2.5 mr-1" />
                          Mark All Done
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-2 text-[10px] text-red-400 hover:bg-red-500/10"
                          onClick={async () => {
                            if (confirm(`Delete all ${pendingDrafts.length} drafts? This cannot be undone.`)) {
                              const draftIds = pendingDrafts.map(d => d.id).filter(Boolean);
                              for (const id of draftIds) {
                                await deleteHistoryItem(id);
                              }
                              loadHistory();
                            }
                          }}
                          data-testid="delete-all-drafts-btn"
                        >
                          <Trash2 className="w-2.5 h-2.5 mr-1" />
                          Delete All
                        </Button>
                      </div>
                    </div>
                    {pendingDrafts.map((draft, idx) => {
                      const complaint = getHistoryDisplayTitle(draft);
                      const age = draft.patient_info?.age || draft.patient_age || '?';
                      const gender = draft.patient_info?.gender || draft.patient_gender || '';
                      const genderDisplay = gender === 'male' ? 'M' : gender === 'female' ? 'F' : '?';
                      const hasTeamNotes = draft.team_notes && draft.team_notes.length > 0;
                      
                      return (
                        <div key={draft.id || idx} className="flex flex-col p-2 rounded bg-blue-500/10 border border-blue-500/20">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Checkbox
                                checked={false}
                                onCheckedChange={() => {
                                  if (draft.id) {
                                    markDraftsAsCompleted([draft.id]);
                                  }
                                }}
                                className="h-4 w-4 shrink-0 border-blue-500 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                                data-testid={`draft-checkbox-${idx}`}
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-medium text-foreground">
                                  {age}yo {genderDisplay} — {complaint}
                                </span>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                  {new Date(draft.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {hasTeamNotes && (
                                <Badge variant="secondary" className="text-[10px] bg-green-500/20 text-green-600">
                                  <MessageSquare className="w-2.5 h-2.5 mr-1" />
                                  {draft.team_notes.length} note{draft.team_notes.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                                onClick={() => {
                                  if (draft.id) deleteHistoryItem(draft.id);
                                }}
                                title="Delete this draft"
                                data-testid={`delete-draft-${idx}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-700"
                                onClick={() => {
                                  loadHistoryItem(draft);
                                  toast.info('Draft loaded! Click "Create Docs" to generate.', { duration: 3000 });
                                }}
                                data-testid={`load-draft-${idx}`}
                              >
                                Load & Edit
                              </Button>
                            </div>
                          </div>
                          {/* Show team notes preview */}
                          {hasTeamNotes && (
                            <div className="mt-2 pt-2 border-t border-green-500/20">
                              <div className="text-[10px] text-green-600 font-medium mb-1">Team additions:</div>
                              {draft.team_notes.slice(0, 2).map((note, i) => (
                                <div key={note.id || i} className="text-[10px] text-muted-foreground truncate">
                                  <span className="text-green-500">{note.author}:</span> {note.note}
                                </div>
                              ))}
                              {draft.team_notes.length > 2 && (
                                <div className="text-[10px] text-muted-foreground">
                                  +{draft.team_notes.length - 2} more...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
            
            {/* Batch Mode Controls */}
            {history.length > 0 && (
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                {isBatchMode ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={selectAllHistory}
                        className="h-7 text-xs"
                      >
                        {batchSelectedItems.size === history.length ? 'Deselect All' : 'Select All'}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {batchSelectedItems.size} selected
                      </span>
                      <span className="text-muted-foreground/30">|</span>
                      <div className="flex items-center gap-1">
                        <CalendarRange className="w-3 h-3 text-muted-foreground" />
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="bg-input border border-border rounded px-1.5 py-0.5 text-[10px] text-foreground w-[110px]"
                          data-testid="batch-date-from"
                        />
                        <span className="text-[10px] text-muted-foreground">to</span>
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="bg-input border border-border rounded px-1.5 py-0.5 text-[10px] text-foreground w-[110px]"
                          data-testid="batch-date-to"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectByDateRange}
                          className="h-6 px-2 text-[10px]"
                          data-testid="batch-select-range"
                        >
                          Select Range
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={batchCopyNotesAndICD10s}
                              disabled={batchSelectedItems.size === 0}
                              className="h-7 text-xs bg-primary hover:bg-primary/90"
                              data-testid="batch-copy-btn"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs max-w-[180px]">
                            <p>Copy all selected notes + ICD-10 codes to clipboard</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                const letterhead = (letterheadLine1 || letterheadLine2 || letterheadAddress)
                                  ? { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress }
                                  : null;
                                batchExportItems('docx', letterhead);
                              }}
                              disabled={batchSelectedItems.size === 0}
                              className="h-7 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                              data-testid="batch-export-docx-btn"
                            >
                              <FileDown className="w-3 h-3 mr-1" />
                              DOCX
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                            <p className="font-medium">Export as Word Document</p>
                            <p className="text-muted-foreground">Combine selected encounters into a single .docx file with AR 25-50 formatting</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                const letterhead = (letterheadLine1 || letterheadLine2 || letterheadAddress)
                                  ? { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress }
                                  : null;
                                batchExportItems('pdf', letterhead);
                              }}
                              disabled={batchSelectedItems.size === 0}
                              className="h-7 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                              data-testid="batch-export-pdf-btn"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                            <p className="font-medium">Export as PDF</p>
                            <p className="text-muted-foreground">Combine selected encounters into a single .pdf file with AR 25-50 formatting</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={batchDeleteItems}
                        disabled={batchSelectedItems.size === 0}
                        className="h-7 text-xs"
                        data-testid="batch-delete-btn"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={exitBatchMode}
                        className="h-7 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 w-full">
                    {/* Search Bar */}
                    <div className="relative w-full">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={effectiveRole === 'patient' ? "Search your letters..." : "Search notes, ICD-10s, complaints..."}
                        value={historySearchQuery}
                        onChange={(e) => setHistorySearchQuery(e.target.value)}
                        className="h-7 pl-7 pr-7 text-xs w-full"
                        data-testid="history-search-input"
                      />
                      {historySearchQuery && (
                        <button
                          onClick={() => setHistorySearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="clear-history-search"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    
                    {/* Search Results Indicator */}
                    {historySearchQuery && (
                      <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 border border-primary/20 rounded text-xs">
                        <Search className="w-3 h-3 text-primary" />
                        <span className="text-muted-foreground">
                          Found <span className="text-primary font-medium">{filteredHistory.length}</span> result{filteredHistory.length !== 1 ? 's' : ''} for "{historySearchQuery}"
                        </span>
                        <button
                          onClick={() => setHistorySearchQuery('')}
                          className="ml-auto text-muted-foreground hover:text-foreground"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                    
                    {/* Date Filter Tabs & Batch Controls - Provider Only */}
                    {effectiveRole === 'provider' && (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-secondary/50 rounded-md p-0.5">
                          <button
                            onClick={() => { setHistoryFilter('all'); setBatchSelectedItems(new Set()); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${historyFilter === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="filter-all"
                          >
                            All
                          </button>
                          <button
                            onClick={() => { setHistoryFilter('today'); setBatchSelectedItems(new Set()); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${historyFilter === 'today' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="filter-today"
                          >
                            Today
                          </button>
                          <button
                            onClick={() => { setHistoryFilter('week'); setBatchSelectedItems(new Set()); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${historyFilter === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="filter-week"
                          >
                            7 Days
                          </button>
                          <button
                            onClick={() => { setHistoryFilter('letters'); setBatchSelectedItems(new Set()); }}
                            className={`px-2 py-1 text-xs rounded transition-colors ${historyFilter === 'letters' ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="filter-letters"
                          >
                            Letters
                          </button>
                          <span className="text-muted-foreground">|</span>
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => setCondenseVersions(!condenseVersions)}
                                  className={`px-2 py-1 text-xs rounded transition-colors ${condenseVersions ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}
                                  data-testid="condense-toggle"
                                >
                                  {condenseVersions ? '📦 Condensed' : '📋 All'}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                                <p className="font-medium">Condense Versions</p>
                                <p className="text-muted-foreground">Group multiple saves of the same encounter into one entry to reduce clutter</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {/* Date jump (calendar) */}
                          {setDateJump && (
                            <label
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
                              title="Jump to a specific encounter date"
                              data-testid="dock-date-jump-label"
                            >
                              <Calendar className="w-3 h-3" />
                              <input
                                type="date"
                                value={dateJump || ''}
                                onChange={(e) => setDateJump(e.target.value)}
                                className="bg-transparent text-foreground text-xs outline-none border-none p-0 cursor-pointer w-[110px] [color-scheme:dark]"
                                data-testid="dock-date-jump-input"
                              />
                              {dateJump && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setDateJump(''); }}
                                  className="text-amber-400 hover:text-amber-300 leading-none"
                                  title="Clear date filter"
                                  data-testid="dock-date-jump-clear"
                                >
                                  ✕
                                </button>
                              )}
                            </label>
                          )}
                          {/* Sort order toggle (no need for batch mode) */}
                          {setSortOrder && (
                            <button
                              type="button"
                              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors text-muted-foreground hover:text-foreground"
                              title={sortOrder === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}
                              data-testid="dock-sort-toggle"
                            >
                              <span className="text-[10px]">{sortOrder === 'desc' ? '↓' : '↑'}</span>
                              <span>{sortOrder === 'desc' ? 'Newest' : 'Oldest'}</span>
                            </button>
                          )}
                        </div>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setIsBatchMode(true)}
                                className="h-7 text-xs"
                                data-testid="batch-mode-btn"
                              >
                                <ClipboardCheck className="w-3 h-3 mr-1" />
                                Batch
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                              <p className="font-medium">Batch Select</p>
                              <p className="text-muted-foreground">Select multiple history items to copy or delete in bulk</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setShowDedup(!showDedup)}
                                className={`h-7 text-xs ${showDedup ? 'bg-amber-500/20 border-amber-500/40' : ''}`}
                                data-testid="dedup-btn"
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Dedup
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                              <p className="font-medium">Deduplicate</p>
                              <p className="text-muted-foreground">Find and merge similar/duplicate history entries to clean up your history</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {todayCount > 0 && (
                          <div className="relative">
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setShowExportMenu(!showExportMenu)}
                                    className="h-7 text-xs border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                                    data-testid="quick-export-today-btn"
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Export Today
                                    <Badge variant="outline" className="ml-1 text-[9px] h-4 px-1 bg-emerald-500/20 border-emerald-500/30 text-emerald-400">
                                      {todayCount}
                                    </Badge>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                                  <p className="font-medium">Quick Export Today</p>
                                  <p className="text-muted-foreground">Export all {todayCount} of today's encounters as a single document — no batch selection needed</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {showExportMenu && (
                              <>
                                <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                                <div className="absolute top-full left-0 mt-1 z-50 bg-card border border-border rounded-lg shadow-lg p-1.5 min-w-[140px]">
                                  <button
                                    onClick={() => {
                                      const lh = (letterheadLine1 || letterheadLine2 || letterheadAddress)
                                        ? { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress }
                                        : null;
                                      quickExportToday('docx', lh);
                                      setShowExportMenu(false);
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-1.5 text-xs rounded hover:bg-blue-500/10 text-blue-400 transition-colors"
                                    data-testid="quick-export-docx"
                                  >
                                    <FileDown className="w-3.5 h-3.5" />
                                    Export as DOCX
                                  </button>
                                  <button
                                    onClick={() => {
                                      const lh = (letterheadLine1 || letterheadLine2 || letterheadAddress)
                                        ? { line1: letterheadLine1, line2: letterheadLine2, address: letterheadAddress }
                                        : null;
                                      quickExportToday('pdf', lh);
                                      setShowExportMenu(false);
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-1.5 text-xs rounded hover:bg-red-500/10 text-red-400 transition-colors"
                                    data-testid="quick-export-pdf"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Export as PDF
                                  </button>
                                  <div className="border-t border-border my-1" />
                                  <button
                                    onClick={() => {
                                      quickExportDrafts && quickExportDrafts('docx');
                                      setShowExportMenu(false);
                                    }}
                                    className="flex items-center gap-2 w-full px-3 py-1.5 text-xs rounded hover:bg-amber-500/10 text-amber-400 transition-colors"
                                    data-testid="export-drafts-docx"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Drafts Input Only (DOCX)
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setShowOrdersReview(true)}
                                className="h-7 text-xs border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                                data-testid="orders-review-btn"
                              >
                                <ClipboardList className="w-3 h-3 mr-1" />
                                EHR Review
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                              <p className="font-medium">EHR Review</p>
                              <p className="text-muted-foreground">Review and sign off on pending orders, referrals, meds, and profiles before entering into EHR</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setShowWeekendCatchup(true)}
                                className="h-7 text-xs border-purple-500/50 text-purple-500 hover:bg-purple-500/10"
                                data-testid="weekend-catchup-btn"
                              >
                                <ListTodo className="w-3 h-3 mr-1" />
                                Catchup
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs max-w-[200px]">
                              <p className="font-medium">Weekend Catchup</p>
                              <p className="text-muted-foreground">Work through delinquent notes oldest-first. Check off as you go, track progress with a completion bar</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Deduplication Panel */}
            {showDedup && (
              <div className="mb-3 p-3 border border-amber-500/20 bg-amber-500/5 rounded-lg">
                <HistoryDedup onMergeComplete={loadHistory} />
              </div>
            )}
            
            {/* History List */}
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No generation history yet</div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No encounters {historyFilter === 'today' ? 'from today' : historyFilter === 'letters' ? 'with letters' : 'in the last 7 days'}
                <button 
                  onClick={() => setHistoryFilter('all')} 
                  className="block mx-auto mt-2 text-xs text-primary hover:underline"
                >
                  Show all {history.length} encounters
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  // Deduplicate: group items with same complaint + time
                  const seen = new Map();
                  const deduped = [];
                  filteredHistory.forEach(item => {
                    const ts = new Date(item.timestamp);
                    const timeKey = `${ts.getHours()}:${ts.getMinutes()}`;
                    const complaint = (item.chief_complaint || item.reason_label || '').substring(0, 40);
                    const key = `${ts.toDateString()}-${timeKey}-${complaint}`;
                    if (!seen.has(key)) {
                      seen.set(key, { ...item, _dupeCount: 1 });
                      deduped.push(seen.get(key));
                    } else {
                      seen.get(key)._dupeCount++;
                    }
                  });

                  // Group by date
                  const dateGroups = {};
                  deduped.forEach(item => {
                    const dateKey = new Date(item.timestamp).toDateString();
                    if (!dateGroups[dateKey]) dateGroups[dateKey] = [];
                    dateGroups[dateKey].push(item);
                  });

                  // Feb 18, 2026 — Coarse buckets so the long history doesn't
                  // scroll endlessly. Each date group lands in exactly one of:
                  // Today / Yesterday / This Week / This Month / Older.
                  // Older starts collapsed.
                  const now = new Date();
                  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const yesterday0 = new Date(today0); yesterday0.setDate(today0.getDate() - 1);
                  const startOfWeek = new Date(today0); startOfWeek.setDate(today0.getDate() - today0.getDay());
                  const startOfMonth = new Date(today0.getFullYear(), today0.getMonth(), 1);
                  const bucketOf = (dateKey) => {
                    const d = new Date(dateKey); d.setHours(0, 0, 0, 0);
                    if (d.getTime() === today0.getTime()) return 'Today';
                    if (d.getTime() === yesterday0.getTime()) return 'Yesterday';
                    if (d >= startOfWeek) return 'This Week';
                    if (d >= startOfMonth) return 'This Month';
                    return 'Older';
                  };
                  const bucketOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'Older'];
                  const buckets = { 'Today': [], 'Yesterday': [], 'This Week': [], 'This Month': [], 'Older': [] };
                  Object.entries(dateGroups).forEach(([dateKey, items]) => {
                    buckets[bucketOf(dateKey)].push({ dateKey, items });
                  });
                  const bucketColors = {
                    'Today':      'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
                    'Yesterday':  'text-sky-400 border-sky-500/30 bg-sky-500/5',
                    'This Week':  'text-amber-400 border-amber-500/30 bg-amber-500/5',
                    'This Month': 'text-purple-300 border-purple-500/30 bg-purple-500/5',
                    'Older':      'text-muted-foreground border-border bg-secondary/30',
                  };
                  const bucketsCollapsed = collapsedDates;

                  return bucketOrder.map((bucketName) => {
                    const groups = buckets[bucketName];
                    if (!groups || groups.length === 0) return null;
                    const totalInBucket = groups.reduce((sum, g) => sum + g.items.length, 0);
                    const bucketKey = `__bucket__${bucketName}`;
                    const bucketCollapsed = bucketsCollapsed.has(bucketKey) || (bucketName === 'Older' && !bucketsCollapsed.has(`__bucket__Older__seen`));
                    return (
                      <div key={bucketName} className="space-y-1.5" data-testid={`inline-history-bucket-${bucketName.toLowerCase().replace(/\s+/g, '-')}`}>
                        <button
                          onClick={() => setCollapsedDates(prev => {
                            const next = new Set(prev);
                            // Track that we've user-interacted with Older so its
                            // collapsed default doesn't keep auto-applying.
                            next.add(`__bucket__Older__seen`);
                            if (next.has(bucketKey)) next.delete(bucketKey); else next.add(bucketKey);
                            return next;
                          })}
                          className={`w-full flex items-center gap-2 px-2 py-1 rounded-lg border ${bucketColors[bucketName]} sticky top-0 z-20 backdrop-blur-sm hover:opacity-90 transition-opacity`}
                          data-testid={`inline-history-bucket-toggle-${bucketName.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <ChevronDown className={`w-3 h-3 transition-transform ${bucketCollapsed ? '-rotate-90' : ''}`} />
                          <span className="text-[10px] font-bold uppercase tracking-wider flex-1 text-left">{bucketName}</span>
                          <Badge variant="outline" className="text-[9px] h-3.5 px-1 font-semibold">{totalInBucket}</Badge>
                        </button>
                        {!bucketCollapsed && groups.map(({ dateKey, items }) => {
                          const dateObj = new Date(dateKey);
                          const isToday = dateObj.toDateString() === new Date().toDateString();
                          const dateLabel = isToday ? 'Today' : dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
                          const isCollapsed = collapsedDates.has(dateKey);
                          // Bulk-sign helper — Feb 24, 2026. Marks every NOT-YET-
                          // closed note in this date bucket as Submitted to Genesis,
                          // dispatching the same per-item flow that the individual
                          // checkbox uses so the unsigned tally + stoplight state
                          // deduct LIVE without reload.
                          const unsignedInGroup = items.filter(i => !(i.completed || i.completed_and_submitted));
                          const allClosed = unsignedInGroup.length === 0;
                          const handleBulkCloseDay = async (e) => {
                            if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                            if (allClosed) return;
                            const n = unsignedInGroup.length;
                            const toastId = toast.loading(`Closing ${n} note${n !== 1 ? 's' : ''}…`);
                            let okCount = 0;
                            for (const itm of unsignedInGroup) {
                              try {
                                await handleSubmittedToggle(itm, true);
                                okCount += 1;
                              } catch { /* ignore one-by-one failures */ }
                            }
                            toast.dismiss(toastId);
                            toast.success(`Closed ${okCount} note${okCount !== 1 ? 's' : ''} on ${dateLabel}`, { duration: 2500 });
                          };
                          return (
                      <div key={dateKey} className="pl-2">
                        <div className="w-full flex items-center gap-2 px-2 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors sticky top-0 bg-background/95 backdrop-blur-sm z-10">
                          <button
                            onClick={() => setCollapsedDates(prev => {
                              const next = new Set(prev);
                              if (next.has(dateKey)) next.delete(dateKey); else next.add(dateKey);
                              return next;
                            })}
                            className="flex items-center gap-2 flex-1 hover:text-foreground transition-colors"
                            data-testid={`date-group-${dateKey}`}
                          >
                            <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                            <Calendar className="w-3 h-3" />
                            <span>{dateLabel}</span>
                            <Badge variant="outline" className="text-[10px] h-4 px-1">{items.length}</Badge>
                            {unsignedInGroup.length > 0 && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/50 text-amber-300 bg-amber-500/10" data-testid={`date-group-unsigned-${dateKey}`}>
                                {unsignedInGroup.length} unsigned
                              </Badge>
                            )}
                          </button>
                          {/* "Check All Closed" — bulk-mark every unsigned note
                              on this date as Submitted to Genesis. Updates live
                              without a page reload. Feb 24, 2026. */}
                          <button
                            onClick={handleBulkCloseDay}
                            disabled={allClosed}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold border transition-colors ${
                              allClosed
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300/70 cursor-default'
                                : 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400/70 cursor-pointer'
                            }`}
                            title={allClosed ? `All ${items.length} notes on ${dateLabel} already submitted` : `Mark all ${unsignedInGroup.length} unsigned notes on ${dateLabel} as Submitted to Genesis`}
                            data-testid={`bulk-close-day-${dateKey}`}
                          >
                            <Check className="w-3 h-3" />
                            <span>{allClosed ? 'All Closed' : `Close ${unsignedInGroup.length}`}</span>
                          </button>
                        </div>
                        {!isCollapsed && items.map((item) => (
                <div key={item.id}>
                    <div 
                      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                        batchSelectedItems.has(item.id) 
                          ? 'border-primary bg-primary/20' 
                          : selectedHistoryItem === item.id 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border bg-secondary/30 hover:border-primary/50 hover:bg-secondary/50'
                      }`} 
                      onClick={() => isBatchMode ? toggleBatchSelect(item.id, { stopPropagation: () => {} }) : loadHistoryItem(item)} 
                      data-testid={`history-item-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        {/* Batch Checkbox */}
                        {isBatchMode && (
                          <div className="flex items-center pt-1">
                            <Checkbox 
                              checked={batchSelectedItems.has(item.id)}
                              onCheckedChange={() => toggleBatchSelect(item.id, { stopPropagation: () => {} })}
                              onClick={(e) => e.stopPropagation()}
                              className="h-5 w-5"
                              data-testid={`batch-checkbox-${item.id}`}
                            />
                          </div>
                        )}

                        {/* Completed & Submitted to MHS Genesis checkbox */}
                        {!isBatchMode && (
                          <div className="flex items-center pt-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={item.completed || item.completed_and_submitted || false}
                              onCheckedChange={(checked) => handleSubmittedToggle(item, !!checked)}
                              className={`h-5 w-5 border-2 ${(item.completed || item.completed_and_submitted) ? 'border-emerald-500 bg-emerald-500/30 data-[state=checked]:bg-emerald-600' : 'border-muted-foreground/50'}`}
                              data-testid={`history-completed-${item.id}`}
                              title={(item.completed || item.completed_and_submitted) ? 'Unmark — remove Submitted to Genesis' : 'Submitted to MHS Genesis — check when note is in the real chart'}
                            />
                          </div>
                        )}
                        
                        {/* Version Count Badge */}
                        {item._versionCount > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedGroups(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(item._groupKey)) {
                                  newSet.delete(item._groupKey);
                                } else {
                                  newSet.add(item._groupKey);
                                }
                                return newSet;
                              });
                            }}
                            className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${
                              expandedGroups.has(item._groupKey) 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                            }`}
                            title={`${item._versionCount} versions - click to ${expandedGroups.has(item._groupKey) ? 'collapse' : 'expand'}`}
                          >
                            v{item._versionCount} {expandedGroups.has(item._groupKey) ? '▼' : '▶'}
                          </button>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {/* Stoplight pill — shows red/yellow/amber/green status */}
                            <StoplightPill
                              encounterId={item.id}
                              fallbackComplete={!!(item.clinical_note)}
                              fallbackSubmitted={!!(item.completed || item.completed_and_submitted)}
                            />
                            {/* SUBMITTED badge with submitted_at tooltip — parity with slide panel */}
                            {(item.completed || item.completed_and_submitted) && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 shrink-0"
                                title={item.submitted_at
                                  ? `Submitted to MHS Genesis · ${new Date(item.submitted_at).toLocaleString()}${item.submitted_by ? ' · ' + item.submitted_by : ''}`
                                  : 'Submitted to MHS Genesis'}
                                data-testid="history-submitted-badge"
                              >
                                SUBMITTED
                              </span>
                            )}
                            {/* Time - prominent + relative "X ago" pill for <24h entries.
                                Feb 27, 2026 — helps you scan the dock to find
                                the chart you JUST closed vs. one from this morning
                                without doing mental arithmetic on the absolute time. */}
                            <span className="text-xs font-bold text-primary tabular-nums shrink-0" data-testid="history-time">
                              {formatHistoryTime(item.timestamp, item.patient_label)}
                            </span>
                            {(() => {
                              const rel = formatRelativeHistoryTime(item.timestamp);
                              return rel ? (
                                <span className="text-[10px] text-emerald-300/80 font-medium shrink-0" data-testid="history-relative-time" title={`Saved ${rel}`}>
                                  {rel}
                                </span>
                              ) : null;
                            })()}
                            <Badge variant="outline" className="text-[10px] bg-secondary border-border shrink-0" data-testid="history-doctype">
                              {getDocTypeLabel(item, effectiveRole)}
                            </Badge>
                            {effectiveRole !== 'patient' && item.patient_status && (
                              <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary shrink-0">
                                {item.patient_status === 'active_duty' ? 'AD' : item.patient_status}
                              </Badge>
                            )}
                            {effectiveRole !== 'patient' && item.icd10_suggestions?.length > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-amber-500/10 border-amber-500/30 text-amber-600 shrink-0">
                                {item.icd10_suggestions.length} ICD-10
                              </Badge>
                            )}
                            {/* Show completion status for orders (provider-only) */}
                            {effectiveRole !== 'patient' && item.imaging_orders?.length > 0 && (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${item.imaging_orders.every(o => o.completed) ? 'bg-green-500/20 border-green-500/30 text-green-500' : 'bg-blue-500/10 border-blue-500/30 text-blue-500'}`}>
                                {item.imaging_orders.filter(o => o.completed).length}/{item.imaging_orders.length} Img{item.imaging_orders.every(o => o.completed) ? ' ✓' : ''}
                              </Badge>
                            )}
                            {effectiveRole !== 'patient' && item.referrals?.length > 0 && (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${item.referrals.every(o => o.completed) ? 'bg-green-500/20 border-green-500/30 text-green-500' : 'bg-purple-500/10 border-purple-500/30 text-purple-500'}`}>
                                {item.referrals.filter(o => o.completed).length}/{item.referrals.length} Ref{item.referrals.every(o => o.completed) ? ' ✓' : ''}
                              </Badge>
                            )}
                            {effectiveRole !== 'patient' && item.proposed_medications?.length > 0 && (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${item.proposed_medications.every(o => o.completed) ? 'bg-green-500/20 border-green-500/30 text-green-500' : 'bg-amber-500/10 border-amber-500/30 text-amber-500'}`}>
                                {item.proposed_medications.filter(o => o.completed).length}/{item.proposed_medications.length} Meds{item.proposed_medications.every(o => o.completed) ? ' ✓' : ''}
                              </Badge>
                            )}
                            {item.proposed_profile && (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${item.proposed_profile.completed ? 'bg-green-500/20 border-green-500/30 text-green-500' : 'bg-orange-500/10 border-orange-500/30 text-orange-500'}`}>
                                Profile{item.proposed_profile.completed ? ' ✓' : ''}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium text-foreground line-clamp-2 mb-1" data-testid="history-title">
                            {getHistoryDisplayTitle(item)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">{formatDate(item.timestamp)}</p>
                          {item.share_url && (
                            <div className="flex items-center gap-1 mt-1">
                              <Link2 className="w-3 h-3 text-green-500" />
                              <span className="text-xs text-green-500 truncate max-w-[150px]">Shared</span>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-5 px-1 text-xs text-green-500 hover:text-green-400 hover:bg-green-500/10" 
                                onClick={async (e) => { e.stopPropagation(); await copyToClipboard(item.share_url); toast.success('Link copied!'); }} 
                                title="Copy share link"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-5 px-1 text-xs text-green-500 hover:text-green-400 hover:bg-green-500/10" 
                                onClick={(e) => { e.stopPropagation(); window.open(item.share_url, '_blank'); }} 
                                title="Open link"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        {!isBatchMode && (
                          <div className="flex flex-col gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-primary shrink-0" 
                              onClick={(e) => { e.stopPropagation(); loadHistoryItem(item); }} 
                              title="Load this document"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {item.admin_letter && !item.share_url && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-green-500 shrink-0" 
                                onClick={(e) => { e.stopPropagation(); shareLetter(item.admin_letter, item.id); }} 
                                title="Create share link"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Versions List */}
                    {item._versionCount > 1 && expandedGroups.has(item._groupKey) && (
                      <div className="ml-4 mt-1 pl-3 border-l-2 border-purple-500/30 space-y-1">
                        {item._allVersions.slice(1).map((version, vIdx) => (
                          <div 
                            key={version.id}
                            onClick={() => loadHistoryItem(version)}
                            className="p-2 rounded bg-secondary/20 hover:bg-secondary/40 cursor-pointer text-xs flex items-center justify-between"
                          >
                            <span className="text-muted-foreground">
                              v{item._versionCount - vIdx - 1} • {new Date(version.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0" 
                              onClick={(e) => { e.stopPropagation(); loadHistoryItem(version); }}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                      </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default React.memo(HistoryPanel);
